class PredictiveSearch extends HTMLFormElement {
  constructor() {
    super();
    this.cachedMap = new Map();
    this.focusElement = this.input;
    this.resetButton.addEventListener('click', this.clear.bind(this));
    this.input.addEventListener('input', FoxTheme.utils.debounce(this.onChange.bind(this), 300));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.searchContent = this.querySelector('.search__content');
    this.searchRecommendationEmpty = this.dataset.searchRecommendationEmpty === 'true';

    const isTemplateSearch = this.closest('.template-search');
    if (isTemplateSearch) {
      document.addEventListener('click', this.handleClickOutside.bind(this));
    }

    // ✅ Empêche que les clics ou soumissions de "Add to Cart" relancent la recherche
    this.addEventListener('click', (event) => {
      const target = event.target.closest('form.product-form, button[type="submit"]');
      if (target) event.stopPropagation();
    });

    // ✅ Gère l'ajout au panier via AJAX (sans redirection)
    this.addEventListener('submit', (event) => {
      if (event.target.matches('form.product-form')) {
        event.preventDefault();

        const form = event.target;
        const formData = new FormData(form);
        const button = form.querySelector('button');

        fetch('/cart/add.js', {
          method: 'POST',
          body: formData,
        })
          .then((res) => {
            if (!res.ok) throw new Error('Erreur ajout panier');
            return res.json();
          })
          .then((data) => {
            if (button) {
              const oldText = button.textContent;
              button.textContent = '✅ Ajouté !';
              button.disabled = true;
              setTimeout(() => {
                button.textContent = oldText;
                button.disabled = false;
              }, 2000);
            }
          })
          .catch((err) => {
            console.error('Erreur panier:', err);
            if (button) button.textContent = '⚠️ Erreur';
          });
      }
    });
  }

  // === Getters ===
  get input() {
    return this.querySelector('input[type="search"]');
  }
  get resetButton() {
    return this.querySelector('button[type="reset"]');
  }

  // === Focus handler ===
  onFocus(event) {
    if (this.closest('.template-search')) {
      document.body.classList.add('predictive-search-open');
      if (this.getQuery().length === 0) return;
      const url = this.setupURL().toString();
      this.renderSection(url, event);
    }
  }

  // === Get search query ===
  getQuery() {
    return this.input.value.trim();
  }

  // === Clear input ===
  clear(event = null) {
    event && event.preventDefault();
    if (this.searchRecommendationEmpty && this.searchContent) {
      this.searchContent.classList.add('hidden');
    }
    this.input.value = '';
    this.input.focus();
    this._updateResultCounters(0, 0);
    this.removeAttribute('results');
  }

  // === Setup URL for fetch ===
  setupURL() {
    const url = new URL(`${window.shopUrl}${FoxTheme.routes.predictive_search_url}`);
    url.searchParams.set('q', this.getQuery());
    url.searchParams.set('resources[limit]', this.dataset.resultsLimit || 10);
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('section_id', FoxTheme.utils.getSectionId(this));
    url.searchParams.set('resources[type]', 'product,article,page,collection');
    return url;
  }

  // === Input change ===
  onChange() {
    if (this.getQuery().length === 0) {
      this.clear();
      return;
    }
    const url = this.setupURL().toString();
    this.renderSection(url);
  }

  // === Render section from cache or fetch ===
  renderSection(url) {
    this.cachedMap.has(url)
      ? this.renderSectionFromCache(url)
      : this.renderSectionFromFetch(url);
  }

  renderSectionFromCache(url) {
    const responseText = this.cachedMap.get(url);
    this.renderSearchResults(responseText);
    this.setAttribute('results', '');
  }

  renderSectionFromFetch(url) {
    this.setAttribute('loading', 'true');
    this.resetButton.classList.add('btn--loading');

    fetch(url)
      .then((response) => {
        if (!response.ok) throw new Error('Network response was not ok');
        return response.text();
      })
      .then((responseText) => {
        this.renderSearchResults(responseText);
        this.cachedMap.set(url, responseText);
      })
      .catch((error) => {
        console.error('Error fetching data: ', error);
        this.setAttribute('error', 'Failed to load data');
      })
      .finally(() => {
        this.removeAttribute('loading');
        this.resetButton.classList.remove('btn--loading');
        this.setAttribute('results', 'true');
      });
  }

  // === Injection du HTML et post-traitement ===
  renderSearchResults(responseText) {
    const id = 'PredictiveSearchResults-' + FoxTheme.utils.getSectionId(this);
    const targetElement = document.getElementById(id);

    if (targetElement) {
      const parser = new DOMParser();
      const parsedDoc = parser.parseFromString(responseText, 'text/html');
      const contentElement = parsedDoc.getElementById(id);

      if (contentElement) {
        this.searchContent?.classList.remove('hidden');
        targetElement.innerHTML = contentElement.innerHTML;

        const query = this.getQuery();
        this.postFilterResults(query);
        this.fetchLocalKeywords(query);
      } else {
        console.error(`Element with id '${id}' not found in parsed response.`);
      }
    } else {
      console.error(`Element with id '${id}' not found in document.`);
    }
  }

    // === Filtrage Shopify natif (corrigé avec filtrage conservé) ===
    postFilterResults(query) {
      const q = this._normalize(query);
      if (!q || !this.searchContent) return;

      const items = this.searchContent.querySelectorAll(
        '.predictive-search__list-item, .predictive-search-result__list-item'
      );

      items.forEach(item => {
        const title = this._normalize(item.textContent || '');
        const keywords = this._normalize(item.dataset.keywords || '');
        const match = title.includes(q) || keywords.includes(q);

        // ✅ Si c’est un élément injecté depuis le JSON local,
        //    on ne le masque JAMAIS
        const isJsonInjected =
          item.closest('#predictive-search-results-article-page-list')?.dataset.jsonInjected === 'true' ||
          item.closest('#predictive-search-results-products-list')?.dataset.jsonInjected === 'true';

        // ✅ Sinon (résultats Shopify natifs), on applique le filtrage classique
        if (!isJsonInjected) {
          item.style.display = match ? '' : 'none';
        }
      });
    }


  // === Recherche locale JSON ===
  fetchLocalKeywords(query) {
    const q = this._normalize(query);
    if (!q) return;

    fetch('/pages/search-keywords-json')
      .then(r => r.json())
      .then(data => {
        const matchesProducts = data.filter(i => i.type === 'product');
        const matchesArticles = data.filter(i => i.type !== 'product');

        const matchedProducts = this._filterMatches(matchesProducts, q);
        const matchedArticles = this._filterMatches(matchesArticles, q);

        if (matchedArticles.length) this.renderLocalArticles(matchedArticles);
        if (matchedProducts.length) this.renderLocalProducts(matchedProducts);
      })
      .catch(err => console.error('Erreur JSON mots-clés:', err));
  }

  // === Filtrage par titre + keywords ===
  _filterMatches(items, q) {
    return items.filter(item => {
      const title = this._normalize(item.title);
      const keywords = Array.isArray(item.keywords)
        ? item.keywords.map(k => this._normalize(k)).join(' ')
        : this._normalize(item.keywords || '');
      return title.includes(q) || keywords.includes(q);
    });
  }

renderLocalArticles(matches) {
  const container = this.searchContent.querySelector('#predictive-search-results-article-page-list');
  if (!container) return;

  // 🔹 Supprimer les anciens résultats JSON
  container.querySelectorAll('[data-json-item="true"]').forEach(el => el.remove());

  // 🔹 Si aucun résultat → afficher un message vide
  if (!matches.length) {
    this._updateResultCounters(0, null);
    return;
  }

  // 🔹 Empêche les doublons
  const existingLinks = Array.from(container.querySelectorAll('a')).map(a => a.href.split('?')[0]);
  const newItems = matches.filter(item => !existingLinks.includes(window.location.origin + item.url));
  if (!newItems.length) return;

  const html = newItems.map(item => `
    <li class="predictive-search-result__list-item flex items-center gap-3" 
        role="option" aria-selected="false" data-json-item="true">
      <a href="${item.url}" class="flex items-center gap-3 align-start">
        ${item.image ? `
          <img src="${item.image}" alt="${item.title}" width="100" height="60" loading="lazy" class="border-radius-5">
        ` : ''}
        <div class="predictive-search-result__list-item-content">
          <div class="block text-sm">${item.title}</div>
          ${item.excerpt ? `<p class="text-sm-extra text-subdued m-0">${item.excerpt}</p>` : ''}
        </div>
      </a>
    </li>
  `).join('');

  container.dataset.jsonInjected = 'true';
  container.insertAdjacentHTML('beforeend', html);
  this._updateResultCounters(null, matches.length);

  // ✅ Masquer "aucun résultat" global si présent
  const emptyState = this.searchContent.querySelector('.empty-state');
  if (emptyState) emptyState.style.display = 'none';
}





renderLocalProducts(matches) {
  let container = this.searchContent.querySelector('#predictive-search-results-products-list');
  if (!container) {
    const sectionParent =
      this.searchContent.querySelector('.predictive-search-result--products') ||
      this.searchContent.querySelector('.search__box.search__results');

    const wrapper = document.createElement('div');
    wrapper.className = 'grid gap-3 predictive-search-result search__box-item predictive-search-result--products';
    wrapper.innerHTML = `
      <h4 class="h5 predictive-search-result__heading">Produits</h4>
      <ul id="predictive-search-results-products-list" class="grid gap-4" role="group" aria-labelledby="predictive-search-products"></ul>
    `;
    sectionParent?.appendChild(wrapper);
    container = wrapper.querySelector('ul');
  }

  // 🔹 Supprimer les anciens résultats JSON
  container.querySelectorAll('[data-json-item="true"]').forEach(el => el.remove());

  // 🔹 Si aucun résultat → afficher message
  if (!matches.length) {
    this._updateResultCounters(null, 0);
    return;
  }

  const html = matches.map(item => {
    const outOfStock = item.available === false;
    return `
      <li class="predictive-search-result__list-item" role="option" aria-selected="false" data-json-item="true">
        <a href="${item.url}" class="flex items-center gap-3 align-start">
          ${item.image ? `<img src="${item.image}" alt="${item.title}" width="60" height="60" loading="lazy">` : ''}
          <div class="predictive-search-result__list-item-content">
            <span>${item.title}</span>
            ${item.catchphrase ? `<span class="text-sm-extra">${item.catchphrase}</span>` : ''}
            ${item.duree_de_la_cure ? `<span class="text-sm-extra">${item.duree_de_la_cure}</span>` : ''}
          </div>
        </a>
        ${
          outOfStock
            ? `<button type="button" class="text-sm btn btn--extra-small btn--secondary predictive-search-btn" disabled>
                 <span class="">Rupture</span><span class="lg:hidden icon-atc"></span>
               </button>`
            : item.first_variant_id
              ? `<button type="button" class="btn btn--extra-small-svg add-to-cart-btn btn--primary predictive-search-btn" data-variant-id="${item.first_variant_id}">
                 <span class="mb:hidden">Ajouter au panier</span><span class="lg:hidden icon-atc"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" class="icon icon-shopping-bag icon--{{ size | default: 'medium' }}" xmlns="http://www.w3.org/2000/svg">
    <path d="M19.575 6.75H4.425C4.24045 6.75087 4.06254 6.81891 3.92451 6.94141C3.78648 7.06391 3.69778 7.23249 3.675 7.41562L2.34375 19.4156C2.33191 19.5202 2.34221 19.6261 2.37396 19.7264C2.40572 19.8267 2.45823 19.9192 2.52808 19.9979C2.59794 20.0766 2.68357 20.1397 2.77941 20.1831C2.87525 20.2266 2.97916 20.2494 3.08438 20.25H20.9156C21.0209 20.2494 21.1248 20.2266 21.2206 20.1831C21.3164 20.1397 21.4021 20.0766 21.4719 19.9979C21.5418 19.9192 21.5943 19.8267 21.626 19.7264C21.6578 19.6261 21.6681 19.5202 21.6563 19.4156L20.325 7.41562C20.3022 7.23249 20.2135 7.06391 20.0755 6.94141C19.9375 6.81891 19.7596 6.75087 19.575 6.75V6.75Z"/>
    <path d="M8.25 6.75C8.25 5.75544 8.64509 4.80161 9.34835 4.09835C10.0516 3.39509 11.0054 3 12 3C12.9946 3 13.9484 3.39509 14.6517 4.09835C15.3549 4.80161 15.75 5.75544 15.75 6.75"/>
  </svg></span>
                 </button>`
              : ''
        }
      </li>`;
  }).join('');

  container.dataset.jsonInjected = 'true';
  container.insertAdjacentHTML('beforeend', html);
  this._updateResultCounters(matches.length, null);
  this._bindAddToCartButtons();
}

// === Met à jour les compteurs dans le conteneur principal ===
_updateResultCounters(productsCount, articlesCount) {
  const wrapper = this.searchContent.querySelector('.search__content-result-list');
  if (!wrapper) return;

  // Récupérer les valeurs actuelles
  const currentProducts = parseInt(wrapper.getAttribute('products')) || 0;
  const currentArticles = parseInt(wrapper.getAttribute('articles')) || 0;

  // Remplacer uniquement les valeurs fournies (même si 0)
  const newProducts = productsCount !== undefined && productsCount !== null ? productsCount : currentProducts;
  const newArticles = articlesCount !== undefined && articlesCount !== null ? articlesCount : currentArticles;

  // Toujours forcer la présence des attributs
  wrapper.setAttribute('products', newProducts);
  wrapper.setAttribute('articles', newArticles);
}


// === Bouton "Ajouter au panier" (via AJAX) ===
_bindAddToCartButtons() {
  const buttons = this.searchContent.querySelectorAll('.add-to-cart-btn');
  buttons.forEach((btn) => {
    if (btn.dataset.bound) return; // éviter double binding
    btn.dataset.bound = true;

    btn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();

      const variantId = btn.dataset.variantId;
      if (!variantId) return;

      btn.disabled = true;
      btn.textContent = 'Ajout...';

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 1 }),
      })
        .then((res) => {
          if (!res.ok) throw new Error('Erreur ajout panier');
          return res.json();
        })
        .then(() => {
          // Recharge la page après l’ajout au panier
          window.location.reload();
        })
        .catch((err) => {
          console.error('Erreur panier:', err);
          btn.textContent = '⚠️ Erreur';
          setTimeout(() => {
            btn.textContent = 'Ajouter au panier';
            btn.disabled = false;
          }, 2000);
        });
    });
  });
}



  // === Réaffiche les résultats et cache le message "vide" ===
  _showResults() {
    const emptyState = this.searchContent.querySelector('.empty-state');
    if (emptyState) emptyState.style.display = 'none';
    const resultsBox = this.searchContent.querySelector('.search__results');
    if (resultsBox) {
      resultsBox.classList.remove('hidden');
      resultsBox.style.display = 'block';
    }
  }

  // === Normalisation ===
  _normalize(str) {
    return (str || '')
      .toString()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/\s+/g, ' ')
      .trim();
  }

  // === Gestion clic extérieur ===
  handleClickOutside(event) {
    if (!this.contains(event.target)) {
      setTimeout(() => document.body.classList.remove('predictive-search-open'));
    }
  }
}

customElements.define('predictive-search', PredictiveSearch, { extends: 'form' });


